// Accessible UI components for better user experience
import React, { forwardRef } from 'react';
import { generateAriaId } from '../../hooks/useAccessibility';

/**
 * Skip Links component for keyboard navigation
 */
export function SkipLinks() {
  return (
    <nav className="skip-links" aria-label="Skip navigation links">
      <a 
        href="#main-content" 
        className="skip-link"
        onFocus={(e) => e.target.classList.add('focused')}
        onBlur={(e) => e.target.classList.remove('focused')}
      >
        Skip to main content
      </a>
      <a 
        href="#main-navigation" 
        className="skip-link"
        onFocus={(e) => e.target.classList.add('focused')}
        onBlur={(e) => e.target.classList.remove('focused')}
      >
        Skip to navigation
      </a>
      <a 
        href="#main-footer" 
        className="skip-link"
        onFocus={(e) => e.target.classList.add('focused')}
        onBlur={(e) => e.target.classList.remove('focused')}
      >
        Skip to footer
      </a>
    </nav>
  );
}

/**
 * Accessible Button component with proper ARIA support
 */
export const AccessibleButton = forwardRef(({ 
  children, 
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  ariaLabel,
  ariaDescribedBy,
  ariaExpanded,
  ariaPressed,
  onClick,
  className = '',
  ...props 
}, ref) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200';
  
  const variantClasses = {
    primary: 'bg-emerald-600 hover:bg-emerald-700 text-white focus:ring-emerald-500',
    secondary: 'bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500',
    outline: 'border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white focus:ring-emerald-500',
    ghost: 'text-gray-300 hover:text-white hover:bg-gray-800 focus:ring-gray-500'
  };
  
  const sizeClasses = {
    small: 'px-3 py-1.5 text-sm',
    medium: 'px-4 py-2 text-base',
    large: 'px-6 py-3 text-lg'
  };
  
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
  const loadingClasses = loading ? 'cursor-wait' : '';
  
  const finalClassName = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${disabledClasses} ${loadingClasses} ${className}`.trim();
  
  const handleClick = (e) => {
    if (disabled || loading) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };
  
  return (
    <button
      ref={ref}
      className={finalClassName}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-expanded={ariaExpanded}
      aria-pressed={ariaPressed}
      onClick={handleClick}
      {...props}
    >
      {loading && (
        <svg 
          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      <span className={loading ? 'sr-only' : ''}>{children}</span>
      {loading && <span className="sr-only">Loading...</span>}
    </button>
  );
});

AccessibleButton.displayName = 'AccessibleButton';

/**
 * Accessible Card component with proper focus management
 */
export function AccessibleCard({ 
  children, 
  title, 
  description,
  href,
  onClick,
  focusable = true,
  className = '',
  ...props 
}) {
  const cardId = generateAriaId('card');
  const titleId = generateAriaId('card-title');
  const descId = generateAriaId('card-desc');
  
  const baseClasses = 'block p-6 bg-gray-800 rounded-lg border border-gray-700 transition-all duration-200';
  const interactiveClasses = (href || onClick) ? 'hover:bg-gray-700 hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-900' : '';
  const finalClassName = `${baseClasses} ${interactiveClasses} ${className}`.trim();
  
  const cardProps = {
    id: cardId,
    className: finalClassName,
    'aria-labelledby': title ? titleId : undefined,
    'aria-describedby': description ? descId : undefined,
    tabIndex: focusable && (href || onClick) ? 0 : -1,
    role: (href || onClick) ? 'button' : undefined,
    onClick: onClick,
    onKeyDown: (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && onClick) {
        e.preventDefault();
        onClick();
      }
    },
    ...props
  };
  
  if (href) {
    return (
      <a href={href} {...cardProps}>
        {title && <h3 id={titleId} className="text-lg font-semibold text-white mb-2">{title}</h3>}
        {description && <p id={descId} className="text-gray-300">{description}</p>}
        {children}
      </a>
    );
  }
  
  return (
    <div {...cardProps}>
      {title && <h3 id={titleId} className="text-lg font-semibold text-white mb-2">{title}</h3>}
      {description && <p id={descId} className="text-gray-300">{description}</p>}
      {children}
    </div>
  );
}

/**
 * Accessible Modal/Dialog component
 */
export function AccessibleModal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  className = '',
  closeOnOverlayClick = true,
  closeOnEscape = true 
}) {
  const titleId = generateAriaId('modal-title');
  
  // Focus trap and escape key handling
  React.useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (closeOnEscape && e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby={titleId}
      aria-modal="true"
      role="dialog"
    >
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={closeOnOverlayClick ? onClose : undefined}
        ></div>
        
        <div className={`inline-block align-bottom bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full ${className}`}>
          <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-start">
              <div className="w-full">
                {title && (
                  <h3 id={titleId} className="text-lg leading-6 font-medium text-white mb-4">
                    {title}
                  </h3>
                )}
                <div className="text-gray-300">
                  {children}
                </div>
              </div>
              <button
                onClick={onClose}
                className="ml-4 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded"
                aria-label="Close modal"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Screen Reader Only text component
 */
export function ScreenReaderOnly({ children, as: Component = 'span' }) {
  return (
    <Component className="sr-only">
      {children}
    </Component>
  );
}

/**
 * Live Region for screen reader announcements
 */
export function LiveRegion({ announcement, priority = 'polite' }) {
  return (
    <div 
      aria-live={priority}
      aria-atomic="true"
      className="sr-only"
    >
      {announcement}
    </div>
  );
}